import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, CalendarHeart, MapPin, Trophy } from "lucide-react";

type Ev = {
  id: string; title: string; description: string | null;
  starts_at: string | null; ends_at: string | null;
  reward_pool_cents: number; location: string | null; status: string; host_id: string;
};

export default function CreatorEvents() {
  const { user } = useAuth();
  const [events, setEvents] = useState<Ev[]>([]);
  const [joined, setJoined] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", starts_at: "", ends_at: "", location: "", reward_pool: "0",
  });

  const load = async () => {
    const { data } = await supabase.from("live_events").select("*").order("starts_at", { ascending: true, nullsFirst: false });
    setEvents((data as Ev[]) || []);
    if (user) {
      const { data: parts } = await supabase.from("event_participants").select("event_id").eq("fan_id", user.id);
      setJoined(new Set((parts as any[] | null)?.map((p) => p.event_id) || []));
    }
  };
  useEffect(() => { load(); }, [user]);

  const submit = async () => {
    if (!user || !form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    const { error } = await supabase.from("live_events").insert({
      host_id: user.id,
      title: form.title.trim(),
      description: form.description.trim() || null,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      location: form.location.trim() || null,
      reward_pool_cents: Math.round(parseFloat(form.reward_pool || "0") * 100),
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Event scheduled");
    setOpen(false);
    setForm({ title: "", description: "", starts_at: "", ends_at: "", location: "", reward_pool: "0" });
    load();
  };

  const rsvp = async (eventId: string) => {
    if (!user) return;
    const { error } = await supabase.from("event_participants").insert({ event_id: eventId, fan_id: user.id });
    if (error) { toast.error(error.message); return; }
    toast.success("You're in");
    load();
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/creator-mode"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold flex-1">Live Events</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm"><Plus className="size-4 mr-1" />New</Button></DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Schedule an event</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2"><Label>Starts</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Ends</Label><Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Studio HQ or stream URL" /></div>
                <div className="space-y-2"><Label>Reward pool (USD)</Label><Input type="number" value={form.reward_pool} onChange={(e) => setForm({ ...form, reward_pool: e.target.value })} /></div>
                <Button onClick={submit} disabled={busy} className="w-full">{busy ? "Saving…" : "Schedule"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {events.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground"><CalendarHeart className="size-10 mx-auto mb-2" />No events yet.</CardContent></Card>
        ) : events.map((e) => {
          const mine = e.host_id === user?.id;
          const isJoined = joined.has(e.id);
          return (
            <Card key={e.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{e.title}</CardTitle>
                  <Badge variant="outline" className="capitalize">{e.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {e.description && <p className="text-muted-foreground">{e.description}</p>}
                {e.starts_at && <p>📅 {new Date(e.starts_at).toLocaleString()}</p>}
                {e.location && <p className="flex items-center gap-1"><MapPin className="size-3.5" />{e.location}</p>}
                <div className="flex items-center justify-between pt-2">
                  <span className="flex items-center gap-1 font-semibold"><Trophy className="size-3.5" />${(e.reward_pool_cents / 100).toFixed(2)} pool</span>
                  {mine ? <Badge>Hosting</Badge> : isJoined ? <Badge variant="secondary">RSVP'd</Badge> : <Button size="sm" onClick={() => rsvp(e.id)}>RSVP</Button>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </main>
    </div>
  );
}
